import { useCallback, useMemo, useRef, useState } from "react";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  OrganizationFilterForm,
  OrganizationFilterFormRef,
  OrganizationFilterFormValues,
} from "@components/Forms/OrganizationFilterForm/OrganizationFilterForm";
import { OrganizationsTable } from "@components/Tables/OrganizationsTable/OrganizationsTable";
import { DEFAULT_ORGANIZATION_TABLE_COLUMN_SETTINGS } from "@components/Tables/OrganizationsTable/UI/OrganizationsTableColumns";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";

const getDefaultFilterValues = (): OrganizationFilterFormValues => ({
  nameSearch: "",
  stytchOrganizationIdSearch: "",
});

const areFiltersActive = (filterValues: OrganizationFilterFormValues): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.stytchOrganizationIdSearch && filterValues.stytchOrganizationIdSearch.trim()),
  );
};

type Props = {
  initialOrganizationTablePageIndex?: number;
};

export const OrganizationsCard = ({
  initialOrganizationTablePageIndex = 0,
}: Props) => {
  const [filterValues, setFilterValues] = useState<OrganizationFilterFormValues>(
    getDefaultFilterValues(),
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const filterFormRef = useRef<OrganizationFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);


  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: OrganizationFilterFormValues) => {
      // Called when form is submitted (Enter key)
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);


  const isFiltersActive = filtersActive;

  return (
    <TableColumnSettingsProvider
      tableId="organizations"
      defaultColumnSettings={DEFAULT_ORGANIZATION_TABLE_COLUMN_SETTINGS}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {isFiltersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={isFiltersActive}
            tooltip="Show filters"
          />
        </Box>
      </PortalHeaderActions>
      
      <OrganizationsTable
        initialPageIndex={initialOrganizationTablePageIndex}
        nameSearch={filterValues.nameSearch}
        stytchOrganizationIdSearch={filterValues.stytchOrganizationIdSearch}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Organization Filters"
      >
        <OrganizationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>
    </TableColumnSettingsProvider>
  );
};
